import { ConfirmRevoke } from './ConfirmRevoke';
import { rememberExtendIntent } from './extendIntent';
import { ExtendSuccess } from './ExtendSuccess';
import {
    getConnectionsNeedingRenewal,
    ReconsentConnection
} from './getReconsentConnections';
import { ReauthExplainer } from './ReauthExplainer';
import { ReconfirmConnections } from './ReconfirmConnections';
import { ReconfirmConsent } from './ReconfirmConsent';
import { ReconsentError } from './ReconsentError';
import {
    trackDismissed,
    trackExtendClicked,
    trackReauthRedirected,
    trackRevokeClicked,
    trackRevokeOutcome
} from './reconsentEvents';
import { ReconsentLoading } from './ReconsentLoading';
import { RemainingConnections, RevokeSuccess } from './RevokeSuccess';
import { useGetReconsentConnections } from './useGetReconsentConnections';
import { useReconsentActions } from './useReconsentActions';
import { TrackingSource } from '../trackingSource';
import { useBankSelectionFlow } from '../useBankSelectionFlow';

type BankSelectionFlow = ReturnType<typeof useBankSelectionFlow>;

const RECONSENT_STEPS = [
    'reconfirmConsent',
    'reconfirm',
    'reconsentLoading',
    'reauth',
    'confirmRevoke',
    'extendSuccess',
    'revokeSuccess',
    'reconsentError'
] as const;

export const isReconsentStep = (step: BankSelectionFlow['step']) =>
    RECONSENT_STEPS.includes(step as (typeof RECONSENT_STEPS)[number]);

export const ReconsentBody = ({
    flow,
    onDone,
    trackingSource
}: {
    flow: BankSelectionFlow;
    onDone: () => void;
    trackingSource: TrackingSource;
}) => {
    const connections = useGetReconsentConnections();

    const connectionsToShow = connections.filter(
        (connection) =>
            connection.isExtendable ||
            flow.extendedConnectionIds.includes(connection.onlineAccountId)
    );

    const connectionsNeedingAction = getConnectionsNeedingRenewal(
        connections,
        flow.extendedConnectionIds
    );

    const { handleExtend, handleConfirmRevoke } = useReconsentActions({
        flow,
        // Deciding here rather than in an effect on the list keeps the list from
        // being rendered on the way to a confirmation.
        onExtended: (extended) => {
            const stillNeedingRenewal = connectionsNeedingAction.filter(
                (connection) =>
                    connection.onlineAccountId !== extended.onlineAccountId
            );

            if (stillNeedingRenewal.length > 0) {
                flow.goToReconfirm();
                return;
            }

            flow.goToExtendSuccess(extended);
        }
    });

    if (flow.step === 'reconfirmConsent') {
        return <ReconfirmConsent onContinue={flow.goToReconfirm} />;
    }

    if (flow.step === 'reconfirm') {
        return (
            <ReconfirmConnections
                connections={connectionsToShow}
                extendedConnectionIds={flow.extendedConnectionIds}
                onExtend={(connection) => {
                    trackExtendClicked({
                        bank_name: connection.bankName,
                        connection_count: connectionsToShow.length
                    });
                    handleExtend(connection);
                }}
                onRevoke={(connection) => {
                    trackRevokeClicked({
                        bank_name: connection.bankName,
                        connection_count: connectionsToShow.length
                    });
                    flow.goToConfirmRevoke(connection);
                }}
                onSkip={() => {
                    trackDismissed({
                        step: 'reconfirm',
                        method: 'not_now',
                        source: trackingSource
                    });
                    onDone();
                }}
            />
        );
    }

    if (flow.step === 'reconsentLoading') {
        return <ReconsentLoading />;
    }

    if (flow.step === 'reauth') {
        return (
            <ReauthExplainer
                onContinue={() => {
                    if (!flow.reauthLink) return;

                    if (flow.reauthConnection) {
                        rememberExtendIntent(
                            flow.reauthConnection.onlineAccountId,
                            trackingSource
                        );
                    }

                    trackReauthRedirected({
                        bank_name: flow.reauthConnection?.bankName
                    });
                    window.location.href = flow.reauthLink;
                }}
            />
        );
    }

    if (flow.step === 'confirmRevoke' && flow.revokeTarget) {
        const target = flow.revokeTarget;

        return (
            <ConfirmRevoke
                connection={target}
                onConfirm={() => handleConfirmRevoke(target)}
                onCancel={() => {
                    trackRevokeOutcome({
                        ob_revoke_outcome: 'cancelled',
                        bank_name: target.bankName
                    });
                    flow.cancelRevoke();
                }}
            />
        );
    }

    if (flow.step === 'extendSuccess' && flow.extendedConnection) {
        return (
            <ExtendSuccess
                connection={flow.extendedConnection}
                onDone={onDone}
            />
        );
    }

    if (flow.step === 'revokeSuccess' && flow.revokeTarget) {
        const revoked = flow.revokeTarget;

        return (
            <RevokeSuccess
                connection={revoked}
                remaining={getRemainingConnections({
                    connections,
                    connectionsNeedingAction,
                    revoked
                })}
                onDone={onDone}
                onBackToConnections={flow.goToReconfirm}
                onConnectBank={flow.goToChoose}
            />
        );
    }

    return (
        <ReconsentError
            onRetry={flow.goToReconfirm}
            onClose={() => {
                trackDismissed({
                    step: 'reconsentError',
                    method: 'not_now',
                    source: trackingSource
                });
                onDone();
            }}
        />
    );
};

const getRemainingConnections = ({
    connections,
    connectionsNeedingAction,
    revoked
}: {
    connections: ReconsentConnection[];
    connectionsNeedingAction: ReconsentConnection[];
    revoked: ReconsentConnection;
}): RemainingConnections => {
    const isNotRevoked = (connection: ReconsentConnection) =>
        connection.onlineAccountId !== revoked.onlineAccountId;

    if (connectionsNeedingAction.some(isNotRevoked)) return 'needs-renewing';
    if (connections.some(isNotRevoked)) return 'up-to-date';

    return 'none';
};
